import React, { useState, useRef, useEffect } from 'react';
import { View, Text, StyleSheet, Platform, ScrollView, TouchableOpacity, Animated, } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useTheme } from '../context/ThemeContext';
import GlossaryPanel from './GlossaryPanel';
import WidgetRenderer from './WidgetRenderer';

// Custom scrollbar styles for web
if (Platform.OS === 'web') {
  const style = document.createElement('style');
  style.textContent = `
    .custom-scrollbar::-webkit-scrollbar {
      width: 6px;
    }
    .custom-scrollbar::-webkit-scrollbar-track {
      background: rgba(255,255,255,0.1);
      border-radius: 3px;
    }
    .custom-scrollbar::-webkit-scrollbar-thumb {
      background: rgba(255,255,255,0.3);
      border-radius: 3px;
    }
    .custom-scrollbar::-webkit-scrollbar-thumb:hover {
      background: rgba(255,255,255,0.5);
    }
  `;
  document.head.appendChild(style);
}

/**
 * RightSidebar - Displays glossary and widgets in independent containers
 * 
 * @param {boolean} showGlossary - Whether to show the glossary section
 * @param {Array} widgets - Array of widget objects to display
 * @param {Object} style - Additional styles
 */
const RightSidebar = ({ showGlossary = true, widgets = [], style }) => {
  const { palette } = useTheme();
  const [isGlossaryCollapsed, setIsGlossaryCollapsed] = useState(true);
  
  const glossaryHeight = useRef(new Animated.Value(0)).current;
  const glossaryOpacity = useRef(new Animated.Value(0)).current;

  const toggleGlossary = () => {
    const newCollapsed = !isGlossaryCollapsed;
    setIsGlossaryCollapsed(newCollapsed);

    Animated.parallel([
      Animated.spring(glossaryHeight, {
        toValue: newCollapsed ? 0 : 800, // Increased height to match left sidebar
        useNativeDriver: false,
        damping: 18,
        stiffness: 140,
      }),
      Animated.timing(glossaryOpacity, {
        toValue: newCollapsed ? 0 : 1,
        duration: newCollapsed ? 150 : 300,
        useNativeDriver: true,
      }),
    ]).start();
  };

  // Group widgets by type
  const widgetsByType = widgets.reduce((acc, widget) => {
    const type = widget.type || 'other';
    if (!acc[type]) acc[type] = [];
    acc[type].push(widget);
    return acc;
  }, {});

  // Get friendly names for widget types
  const getWidgetTypeLabel = (type) => {
    const labels = {
      'file': '📁 Course Materials',
      'spreadsheet': '📊 Worksheets & Trackers',
      'quiz': '❓ Quizzes',
      'sequential_quiz': '🎯 Sequential Quiz',
      'matching': '🎯 Interactive Matching',
      'calculator': '🧮 Calculators',
      'scorecard': '📈 Self-Assessment',
      'checklist': '✅ Checklists',
      'decision_tree': '🌳 Decision Guides',
      'timeline': '📅 Timelines',
      'comparison': '⚖️ Comparisons',
      'other': '🧩 Additional Tools',
    };
    return labels[type] || '🧩 Additional Tools';
  };

  // Determine what to show
  const hasContent = showGlossary || widgets.length > 0;

  if (!hasContent) return null;

  return (
    <View style={[styles.sidebarContainer]}>
      <ScrollView 
        style={styles.scrollContent}
        contentContainerStyle={styles.scrollContentContainer}
        showsVerticalScrollIndicator={true}
      >
        {/* Glossary Section - Collapsible Container */}
        {showGlossary && (
          <View style={[styles.independentContainer, { 
            backgroundColor: '#333333', 
                        ...Platform.select({
            web: {
                boxShadow: '5px 7px 0px rgba(0,0,0,1)',
            },
            default: {
                shadowColor: '#000000',
                shadowOffset: { width: 5, height: 7 },
                shadowOpacity: 1,
                shadowRadius: 0,
                elevation: 8,
            },
        }),
          }]}>
            <TouchableOpacity 
              style={styles.collapsibleHeader}
              onPress={toggleGlossary}
              activeOpacity={0.7}
            >
              <Text style={[styles.sectionTitle, { color: '#F1F1F1'}]}>
                Business Glossary
              </Text>
              <MaterialCommunityIcons
                name={isGlossaryCollapsed ? 'chevron-down' : 'chevron-up'}
                size={24}
                color={'#F1F1F1'}
              />
            </TouchableOpacity>
            
            <Animated.View
              style={{
                height: glossaryHeight,
                opacity: glossaryOpacity,
                overflow: 'hidden',
              }}
            >
              <View 
                className="custom-scrollbar"
                style={{ 
                  maxHeight: '100%', 
                  overflow: 'auto'
                }}
              >
                <GlossaryPanel 
                  hideHeader={true}
                  style={{ 
                    borderWidth: 0, 
                    backgroundColor: 'transparent'
                  }} 
                />
              </View>
            </Animated.View>
          </View>
        )}

        {/* Widgets Section - Each widget type in its own container */}
        {widgets.length > 0 && (
          <>
            {Object.entries(widgetsByType).map(([type, typeWidgets]) => (
              <View key={type} style={[styles.independentContainer, styles.widgetContainer]}>
                <View style={styles.sectionHeader}>
                  <Text style={[styles.widgetSectionTitle]}>
                    {getWidgetTypeLabel(type)}
                  </Text>
                </View>
                {typeWidgets.map((widget, index) => (
                  <View key={widget.id || index} style={styles.widgetWrapper}>
                    <WidgetRenderer widget={widget} compact={true} />
                  </View>
                ))}
              </View>
            ))}
          </>
        )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  sidebarContainer: {
    width: 360,
    overflow: 'visible',
  },
  scrollContent: {
    flex: 1,
    overflow: 'visible',
  },
  scrollContentContainer: {
    gap: 20,
    paddingBottom: 20,
    overflow: 'visible',
  },
  independentContainer: {
    borderRadius: 20,
    padding: 22,
    marginRight: 8,
  },
  widgetContainer: {
    backgroundColor: '#FFFFFF',
    borderWidth: 3,
    borderColor: '#000000',
    ...Platform.select({
      web: {
        boxShadow: '5px 7px 0px rgba(0,0,0,1)',
      },
    }),
  },
  sectionHeader: {
    marginBottom: 16,
  },
  collapsibleHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  sectionTitle: {
    fontSize: 22,
    fontFamily: 'Chivo',
    fontWeight: '500',
        ...Platform.select({
        web: {
            textShadow: '2px 2px 0px rgba(0,0,0,1)',
        },
        default: {
            textShadowColor: 'rgba(0,0,0,1)',
            textShadowOffset: { width: 2, height: 2 },
            textShadowRadius: 1,
        },
    }),
  },
  widgetSectionTitle: {
    fontSize: 18,
    fontFamily: 'Chivo',
    fontWeight: '600',
    color: '#1A1A1A',
  },
  widgetWrapper: {
    marginBottom: 16,
  },
});

export default RightSidebar;

